import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { startLoading, stopLoading } from "./LoadingSlice";
import api from "../../api";
import publicAppsClient from "../../api/middleware/publicAppsClient";
import {
  launchGameRequest,
  launchSportsRequest,
} from "../../api/services/games";
import { normalizeBanners } from "../../utils/normalizeBanner";

// banners, categories, providers and the games grid all render their own
// inline loading states, so none of them use the blocking full-page loader
export const fetchTopBanners = createAsyncThunk(
  "game/fetchTopBanners",
  async () => {
    try {
      const response = await publicAppsClient.get("banners");
      return { data: normalizeBanners(response.data?.data) };
    } catch (error) {
      throw Error("Error fetching banners");
    }
  }
);
export const fetchCategories = createAsyncThunk(
  "game/getCategories",
  async () => {
    try {
      const response = await publicAppsClient.get(`/categories`);
      return response.data;
    } catch (error) {
      throw Error("Error fetching categories");
    }
  }
);
export const fetchProviders = createAsyncThunk(
  "game/getProviders",
  async () => {
    try {
      const response = await publicAppsClient.get(`/providers`);
      return response.data;
    } catch (error) {
      throw Error("Error fetching providers");
    }
  }
);
export const fetchSubCategories = createAsyncThunk(
  "game/getSubCategories",
  async (id, { dispatch }) => {
    try {
      const response = await api.get(`/games/subCategories?categoryId=${id}`);
      return response.data;
    } catch (error) {
      // Handle error condition
      throw Error("Error fetching user data");
    } finally {
    }
  }
);
export const fetchGames = createAsyncThunk(
  "game/getGames",
  async (data, { dispatch }) => {
    dispatch(startLoading());
    let params = "?";
    for (const key in data) {
      params += `${key}=${data[key]}&`;
    }
    try {
      const response = await api.get(`/games/list${params}`);
      return response.data;
    } catch (error) {
      // Handle error condition
      throw Error("Error fetching user data");
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const getGameUrl = createAsyncThunk(
  "game/getGameUrl",
  async (id, { dispatch }) => {
    dispatch(startLoading());
    try {
      localStorage.removeItem("isUserPlaying");
      const response = await api.get(`/games/getGameUrl?openTableId=${id}`);
      return response.data;
    } catch (error) {
      throw Error(
        error.response?.data?.error ||
        error.message ||
        "Error fetching game URL"
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
export const getAviatrixUrl = createAsyncThunk(
  "game/getAviatrixUrl",
  async (_, { dispatch }) => {
    dispatch(startLoading());
    try {
      localStorage.removeItem("isUserPlaying");
      const response = await api.get(`/games/getAviatrixGameUrl`);
      return response.data;
    } catch (error) {
      throw Error(
        error.response?.data?.error ||
        error.message ||
        "Error fetching Aviatrix game URL"
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);
// the play page renders its own launch screen, so no blocking loader here
export const launchGame = createAsyncThunk(
  "game/launchGame",
  async (launchParams) => {
    try {
      localStorage.removeItem("isUserPlaying");
      const response = await launchGameRequest(launchParams);
      // swagger documents data.url; the launcher doc shows data.launch_url
      const launchUrl = response?.data?.launch_url || response?.data?.url;
      if (!launchUrl) throw new Error("Launch URL missing in response");
      return { launchUrl };
    } catch (error) {
      throw Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Error launching game"
      );
    }
  }
);
// sports shares the launch state with games, so PlayGame renders both
export const launchSports = createAsyncThunk("game/launchSports", async () => {
  try {
    localStorage.removeItem("isUserPlaying");
    const response = await launchSportsRequest();
    const data = response?.data?.data ?? response?.data ?? response;
    const launchUrl =
      typeof data === "string"
        ? data
        : data?.launchURL || data?.launchUrl || data?.launch_url || data?.url;
    if (!launchUrl) throw new Error("Launch URL missing in response");
    return { launchUrl };
  } catch (error) {
    throw Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Error launching sports"
    );
  }
});
export const searchGames = createAsyncThunk(
  "game/searchGames",
  async (data, { dispatch }) => {
    try {
      const response = await publicAppsClient.get(`/games`, {
        params: { limit: 20, offset: 0, search: data.search },
      });
      return { data: response.data?.data || [] };
    } catch (error) {
      throw Error("Error searching games");
    }
  }
);

export const getAllGames = createAsyncThunk(
  "game/getAllGames",
  async (data, { dispatch }) => {
    dispatch(startLoading());

    // let params = "?";
    // for (const key in data) {
    //   params += `${key}=${data[key]}&`;
    // }
    try {
      // TODO: Re-enable when /games/listAll API is ready
      // const response = await api.get(`/games/listAll${params}`);
      // return response.data;
    } catch (error) {
      // Handle error condition
      throw Error("Error fetching user data");
    } finally {
      dispatch(stopLoading());
    }
  }
);


export const getGames = createAsyncThunk(
  "game/games",
  async (filters = {}) => {
    const { offset = 0, limit = 20, categories = [], providers = [], search = "" } = filters;
    try {
      // /games accepts a single game_type_id / provider_id per request,
      // so fan out one request per selected pair and merge the pages.
      const categoryIds = categories.length ? categories : [null];
      const providerIds = providers.length ? providers : [null];
      const requests = categoryIds.flatMap((categoryId) =>
        providerIds.map((providerId) =>
          publicAppsClient.get(`/games`, {
            params: {
              limit,
              offset,
              ...(search && { search }),
              ...(categoryId && { game_type_id: categoryId }),
              ...(providerId && { provider_id: providerId }),
            },
          })
        )
      );
      const responses = await Promise.all(requests);
      return {
        offset,
        data: responses.flatMap((res) => res.data?.data || []),
        total: responses.reduce((sum, res) => sum + (res.data?.total || 0), 0),
      };
    } catch (error) {
      throw Error("Error fetching games");
    }
  }
);
// Create a slice for your API-related actions and reducers
const gameSlice = createSlice({
  name: "game",
  initialState: {
    data: null,
    error: null,
    categories: [],
    categoriesLoading: false,
    gamesData: null,
    subCategories: [],
    subCategoryLoader: false,
    providers: [],
    gameUrl: null,
    aviatrixUrl: null,
    launchUrl: null,
    launchLoading: false,
    launchError: null,
    searchedGames: null,
    allGames: [],
    totalGames: 0,
    // starts true so the grid shows its spinner (not the empty state) on first paint
    gamesLoading: true,
    gameFilters: { categories: [], providers: [], search: "" },
    filterApplied: false,
    filters: {},
  },
  reducers: {
    resetLaunch(state) {
      state.launchUrl = null;
      state.launchLoading = false;
      state.launchError = null;
    },
    setGameFilters(state, action) {
      state.gameFilters = { ...state.gameFilters, ...action.payload };
    },
    resetFilterStatus(state) {
      state.filters = {};
      state.filterApplied = false;
    },
    checkFilterStatus(state, action) {
      state.filterApplied = true;
      state.filters = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopBanners.fulfilled, (state, action) => {
        state.banners = action.payload?.data || [];
      })
      .addCase(fetchTopBanners.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.data;
        state.categoriesLoading = false;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.error = action.error.message;
        state.categoriesLoading = false;
      })
      .addCase(fetchGames.fulfilled, (state, action) => {
        state.gamesData = action.payload.data;
      })
      .addCase(fetchGames.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchSubCategories.pending, (state, action) => {
        state.subCategoryLoader = true;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.subCategories = action.payload.data;
        state.subCategoryLoader = false;
      })
      .addCase(fetchSubCategories.rejected, (state, action) => {
        state.error = action.error.message;
        state.subCategoryLoader = false;
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.providers = action.payload.data;
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(getGameUrl.fulfilled, (state, action) => {
        state.gameUrl = action.payload.data;
      })
      .addCase(getGameUrl.rejected, (state, action) => {
        state.error = "Error - " + action.error.message;
      })
      .addCase(getAviatrixUrl.fulfilled, (state, action) => {
        state.aviatrixUrl = action.payload.data;
      })
      .addCase(getAviatrixUrl.rejected, (state, action) => {
        state.error = "Error - " + action.error.message;
      })
      .addCase(launchGame.pending, (state) => {
        state.launchLoading = true;
        state.launchError = null;
        state.launchUrl = null;
      })
      .addCase(launchGame.fulfilled, (state, action) => {
        state.launchUrl = action.payload.launchUrl;
        state.launchLoading = false;
      })
      .addCase(launchGame.rejected, (state, action) => {
        state.launchError = action.error.message;
        state.launchLoading = false;
      })
      .addCase(launchSports.pending, (state) => {
        state.launchLoading = true;
        state.launchError = null;
        state.launchUrl = null;
      })
      .addCase(launchSports.fulfilled, (state, action) => {
        state.launchUrl = action.payload.launchUrl;
        state.launchLoading = false;
      })
      .addCase(launchSports.rejected, (state, action) => {
        state.launchError = action.error.message;
        state.launchLoading = false;
      })
      .addCase(searchGames.fulfilled, (state, action) => {
        state.searchedGames = action.payload.data;
      })
      .addCase(searchGames.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(getGames.pending, (state) => {
        state.gamesLoading = true;
      })
      .addCase(getGames.fulfilled, (state, action) => {
        const { data, total, offset } = action.payload;
        const merged = offset ? [...state.allGames, ...data] : data;
        // merged per-filter pages can overlap, so dedupe by id
        state.allGames = [...new Map(merged.map((g) => [g.id, g])).values()];
        // an empty page means no more results, whatever the reported total
        state.totalGames = data.length ? total : state.allGames.length;
        state.gamesLoading = false;
      })
      .addCase(getGames.rejected, (state, action) => {
        state.error = action.error.message;
        state.gamesLoading = false;
      })
      // string types instead of an import: UserSlice owns these thunks
      .addMatcher(
        (action) =>
          action.type === "user/addFavourite/fulfilled" ||
          action.type === "user/removeFavourite/fulfilled",
        (state, action) => {
          const { gameId, isFavorite } = action.payload;
          state.allGames = state.allGames.map((g) =>
            String(g.id) === String(gameId) ? { ...g, is_favorite: isFavorite } : g
          );
        }
      );
  },
});

// Export the async thunk action creator and the reducer
export const { checkFilterStatus, resetFilterStatus, setGameFilters, resetLaunch } =
  gameSlice.actions;
export const { reducer } = gameSlice;
export default gameSlice.reducer;
